
import React, { useMemo, useState } from "react";
import { useLeads } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import {
  Users,
  PhoneCall,
  CheckCircle,
  Clock,
  TrendingUp,
  Percent,
  Target,
  LayoutDashboard,
  Calendar,
  Zap,
  AlertTriangle,
  MessageSquare,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import StatisticsCard from "@/components/dashboard/StatisticsCard";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addMonths, format, isSameMonth, parseISO, subMonths } from "date-fns";
import { el } from "date-fns/locale";

const Dashboard = () => {
  const { leads } = useLeads();
  const { partners } = usePartners();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("3m");

  // Calculate statistics
  const totalLeads = leads.length;
  const pendingLeads = leads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
  const successLeads = leads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
  const infoLeads = leads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length;

  // Get date range based on selection
  const getDateRange = () => {
    const now = new Date();
    let startDate;

    switch (timeRange) {
      case "3m":
        startDate = subMonths(now, 3);
        break;
      case "6m":
        startDate = subMonths(now, 6);
        break;
      case "1y":
        startDate = subMonths(now, 12);
        break;
      default:
        startDate = subMonths(now, 3);
    }

    return { startDate, endDate: now };
  };

  // Filter leads by date range
  const { startDate, endDate } = getDateRange();
  const filteredLeads = leads.filter(lead => {
    const leadDate = parseISO(lead.createdAt);
    return leadDate >= startDate && leadDate <= endDate;
  });

  const filteredStats = useMemo(() => {
    const total = filteredLeads.length;
    const success = filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
    const pending = filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
    const info = filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length;
    const unassigned = filteredLeads.filter(lead => !lead.partnerId).length;
    const conversion = total > 0 ? Math.round((success / total) * 100) : 0;
    return { total, success, pending, info, unassigned, conversion };
  }, [filteredLeads]);

  const weeklyPulse = useMemo(() => {
    const now = new Date();
    const last7 = subMonths(now, 0);
    const currentWindowStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const previousWindowStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    const current = leads.filter(lead => {
      const date = parseISO(lead.createdAt);
      return date >= currentWindowStart && date <= last7;
    }).length;

    const previous = leads.filter(lead => {
      const date = parseISO(lead.createdAt);
      return date >= previousWindowStart && date < currentWindowStart;
    }).length;

    const delta = previous > 0 ? Math.round(((current - previous) / previous) * 100) : 0;
    return { current, previous, delta };
  }, [leads]);

  const actionableAlerts = useMemo(() => {
    const stalePending = filteredLeads.filter(lead => {
      if (lead.stage !== "σεΕκκρεμότητα") return false;
      const ageHours = (Date.now() - parseISO(lead.createdAt).getTime()) / (1000 * 60 * 60);
      return ageHours >= 48;
    }).length;

    const failedSms = filteredLeads.filter(lead => lead.smsStatus === "failed").length;
    const noPartner = filteredLeads.filter(lead => !lead.partnerId).length;

    return { stalePending, failedSms, noPartner };
  }, [filteredLeads]);

  // Prepare chart data
  const serviceChartData = [
    { name: "Εξοικονομώ", count: filteredLeads.filter(lead => lead.service === "εξοικονομώ").length, fill: "#3b82f6" },
    { name: "Αντλίες", count: filteredLeads.filter(lead => lead.service === "αντλίεςΘερμότητας").length, fill: "#8b5cf6" },
    { name: "Φωτοβολταϊκά", count: filteredLeads.filter(lead => lead.service === "φωτοβολταϊκά").length, fill: "#10b981" },
    { name: "Κλιματιστικά", count: filteredLeads.filter(lead => lead.service === "κλιματιστικά").length, fill: "#f59e0b" },
    { name: "Φυσικό Αέριο", count: filteredLeads.filter(lead => lead.service === "φυσικόΑέριο").length, fill: "#ef4444" },
    { name: "Ηλιακά", count: filteredLeads.filter(lead => lead.service === "ηλιακά").length, fill: "#14b8a6" },
    { name: "Άλλο", count: filteredLeads.filter(lead => lead.service === "άλλο").length, fill: "#6b7280" },
  ];

  // Prepare stage data for pie chart
  const stageData = [
    { name: "Σε εκκρεμότητα", value: filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length, color: "#f59e0b" },
    { name: "Επιτυχία", value: filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length, color: "#10b981" },
    { name: "Πληροφορίες", value: filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length, color: "#6366f1" },
  ];

  // Prepare monthly trend data
  const getMonthlyTrendData = () => {
    const months = [];
    let currentMonth = new Date(startDate);

    while (currentMonth <= endDate) {
      months.push(new Date(currentMonth));
      currentMonth = addMonths(currentMonth, 1);
    }

    return months.map(monthDate => {
      const monthLeads = leads.filter(lead => {
        const leadDate = parseISO(lead.createdAt);
        return isSameMonth(leadDate, monthDate);
      });
      const successCount = monthLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;

      return {
        name: format(monthDate, 'MMM', { locale: el }),
        total: monthLeads.length,
        success: successCount,
      };
    });
  };

  const monthlyTrendData = getMonthlyTrendData();

  // Calculate partner performance
  const partnerPerformance = partners.map(partner => {
    const partnerLeads = filteredLeads.filter(lead => lead.partnerId === partner.id);
    const successfulLeads = partnerLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
    const conversionRate = partnerLeads.length > 0
      ? Math.round((successfulLeads / partnerLeads.length) * 100)
      : 0;

    return {
      name: partner.name.length > 15 ? partner.name.substring(0, 15) + '...' : partner.name,
      leads: partnerLeads.length,
      success: successfulLeads,
      rate: conversionRate,
    };
  }).sort((a, b) => b.leads - a.leads).slice(0, 5);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Πίνακας Ελέγχου"
        description="Επισκόπηση απόδοσης και leads"
        icon={<LayoutDashboard />}
        iconGradient="blue"
        actions={
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-48 bg-white border-slate-200">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Περίοδος" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3m">Τελευταίοι 3 μήνες</SelectItem>
              <SelectItem value="6m">Τελευταίοι 6 μήνες</SelectItem>
              <SelectItem value="1y">Τελευταίος χρόνος</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatisticsCard
          title="Συνολικές Επαφές"
          value={filteredStats.total}
          description="Στην επιλεγμένη περίοδο"
          icon={<PhoneCall className="h-5 w-5" />}
          variant="info"
          trend={weeklyPulse.previous > 0 ? { value: weeklyPulse.delta, isPositive: weeklyPulse.delta >= 0 } : undefined}
        />

        <StatisticsCard
          title="Συνεργάτες"
          value={partners.length}
          description="Ενεργοί συνεργάτες"
          icon={<Users className="h-5 w-5" />}
          variant="default"
        />

        <StatisticsCard
          title="Επιτυχημένες"
          value={filteredStats.success}
          description="Ολοκληρωμένες"
          icon={<CheckCircle className="h-5 w-5" />}
          variant="success"
        />

        <StatisticsCard
          title="Σε Αναμονή"
          value={filteredStats.pending}
          description="Σε εκκρεμότητα"
          icon={<Clock className="h-5 w-5" />}
          variant="warning"
        />

        <StatisticsCard
          title="Επιτυχία"
          value={`${filteredStats.conversion}%`}
          description="Ποσοστό μετατροπής"
          icon={<Percent className="h-5 w-5" />}
          variant={filteredStats.conversion > 50 ? "success" : "default"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Γρήγορες Ενέργειες</CardTitle>
            <CardDescription>Συχνές ενέργειες διαχείρισης</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button onClick={() => navigate("/admin/leads")} className="gap-2">
              <PhoneCall className="h-4 w-4" /> Διαχείριση Leads
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/messages")} className="gap-2">
              <MessageSquare className="h-4 w-4" /> Παρακολούθηση Μηνυμάτων
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/reports")} className="gap-2">
              <TrendingUp className="h-4 w-4" /> Αναφορές
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin/leads/assignment")} className="gap-2">
              <Users className="h-4 w-4" /> Ανάθεση Leads
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Alerts
            </CardTitle>
            <CardDescription>Περιοχή που θέλει προσοχή</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <span className="text-sm text-slate-600">Μη ανατεθειμένα</span>
              <Badge variant={actionableAlerts.noPartner > 0 ? "secondary" : "outline"}>{actionableAlerts.noPartner}</Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <span className="text-sm text-slate-600">Pending {">"} 48h</span>
              <Badge variant={actionableAlerts.stalePending > 0 ? "secondary" : "outline"}>{actionableAlerts.stalePending}</Badge>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <span className="text-sm text-slate-600">SMS αποτυχίες</span>
              <Badge variant={actionableAlerts.failedSms > 0 ? "destructive" : "outline"}>{actionableAlerts.failedSms}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Τάση Επαφών</CardTitle>
                <CardDescription>Μηνιαία εξέλιξη leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyTrendData}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="total" name="Επαφές" stroke="#3b82f6" fillOpacity={1} fill="url(#colorTotal)" />
                <Area type="monotone" dataKey="success" name="Επιτυχίες" stroke="#10b981" fillOpacity={1} fill="url(#colorSuccess)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-purple-50">
                <Target className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Κατάσταση Leads</CardTitle>
                <CardDescription>Κατανομή ανά στάδιο</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={4}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#94a3b8' }}
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [value, "Επαφές"]}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-rose-50">
              <ArrowRight className="h-4 w-4 text-rose-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Pipeline Snapshot</CardTitle>
              <CardDescription>Ροή από lead σε επιτυχία</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">Επιτυχημένες / Σύνολο</span><span className="font-medium">{filteredStats.success}/{filteredStats.total || 0}</span></div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, filteredStats.conversion)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">Σε Εκκρεμότητα / Σύνολο</span><span className="font-medium">{filteredStats.pending}/{filteredStats.total || 0}</span></div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, filteredStats.total > 0 ? Math.round((filteredStats.pending / filteredStats.total) * 100) : 0)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">Ήθελε Πληροφορίες / Σύνολο</span><span className="font-medium">{filteredStats.info}/{filteredStats.total || 0}</span></div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, filteredStats.total > 0 ? Math.round((filteredStats.info / filteredStats.total) * 100) : 0)}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-amber-50">
                <Zap className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Leads ανά Υπηρεσία</CardTitle>
                <CardDescription>Κατανομή ενδιαφέροντος</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serviceChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-emerald-50">
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Top Συνεργάτες</CardTitle>
                <CardDescription>Κορυφαίοι 5 σε leads</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={partnerPerformance}
                layout="vertical"
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Legend />
                <Bar dataKey="leads" name="Leads" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="success" name="Επιτυχίες" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
